import { useEditorStore } from '@/store/editor-store'
import { useProjectStore } from '@/store/project-store'
import { PauseIcon as Pause, PlayIcon as Play, Film01Icon as Film, ScissorIcon as Scissors, Delete02Icon as Trash2, UndoIcon as Undo2, RedoIcon as Redo2, ZoomInAreaIcon as ZoomIn, TextIcon as Type, Camera01Icon as Camera, MusicNote02Icon as Music, Maximize01Icon as Maximize2, VolumeHighIcon as Volume2, Upload01Icon as Upload } from 'hugeicons-react'
import { useRef, useEffect, useState, useCallback } from 'react'

const TIMELINE_PADDING = 8 // Horizontal padding for timeline start/end
const SNAP_THRESHOLD_PX = 8 // Distance in pixels to snap to clip bounds or playhead

// Custom inline SVGs for modern eye/speaker toggles
const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
)

const EyeOffIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12c1.387 4.178 5.322 7.178 9.963 7.178.966 0 1.905-.132 2.8-.38m2.164-2.164C18.423 15.69 19.5 14.05 19.5 12c0-4.638-3.007-8.573-7.178-9.963a10.477 10.477 0 0 0-3.328-.528M3 3l18 18" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 9a3 3 0 0 1 3 3m-3 3a3 3 0 0 1-3-3" /></svg>
)

const SpeakerIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" /></svg>
)

const SpeakerOffIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6 4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" /></svg>
)

export default function Timeline() {
    // Optimized Selectors
    const duration = useEditorStore(state => state.duration)
    const currentTime = useEditorStore(state => state.currentTime)
    const setCurrentTime = useEditorStore(state => state.setCurrentTime)
    const zoomRegions = useEditorStore(state => state.zoomRegions)
    const addZoomRegion = useEditorStore(state => state.addZoomRegion)
    const updateZoomRegion = useEditorStore(state => state.updateZoomRegion)
    const removeZoomRegion = useEditorStore(state => state.removeZoomRegion)
    const clips = useEditorStore(state => state.clips)
    const splitClip = useEditorStore(state => state.splitClip)
    const deleteClip = useEditorStore(state => state.deleteClip)
    const updateClip = useEditorStore(state => state.updateClip)
    const removeGap = useEditorStore(state => state.removeGap)
    const isPlaying = useEditorStore(state => state.isPlaying)
    const setIsPlaying = useEditorStore(state => state.setIsPlaying)
    const selectedClipId = useEditorStore(state => state.selectedClipId)
    const setSelectedClip = useEditorStore(state => state.setSelectedClip)
    const thumbnails = useEditorStore(state => state.thumbnails)
    const sourceDuration = useEditorStore(state => state.sourceDuration)
    const textLayers = useEditorStore(state => state.textLayers)
    const addTextLayer = useEditorStore(state => state.addTextLayer)
    const updateTextLayer = useEditorStore(state => state.updateTextLayer)
    const removeTextLayer = useEditorStore(state => state.removeTextLayer)
    const selectedTextLayerId = useEditorStore(state => state.selectedTextLayerId)
    const setSelectedTextLayer = useEditorStore(state => state.setSelectedTextLayer)
    const elementLayers = useEditorStore(state => state.elementLayers)
    // const addElementLayer = useEditorStore(state => state.addElementLayer)
    const updateElementLayer = useEditorStore(state => state.updateElementLayer)
    const removeElementLayer = useEditorStore(state => state.removeElementLayer)
    const selectedElementLayerId = useEditorStore(state => state.selectedElementLayerId)
    const setSelectedElementLayer = useEditorStore(state => state.setSelectedElementLayer)
    const cameraLayer = useEditorStore(state => state.cameraLayer)
    const updateCameraLayer = useEditorStore(state => state.updateCameraLayer)
    const audioLayers = useEditorStore(state => state.audioLayers)
    const addAudioLayer = useEditorStore(state => state.addAudioLayer)
    const updateAudioLayer = useEditorStore(state => state.updateAudioLayer)
    const removeAudioLayer = useEditorStore(state => state.removeAudioLayer)
    const selectedAudioLayerId = useEditorStore(state => state.selectedAudioLayerId)
    const setSelectedAudioLayer = useEditorStore(state => state.setSelectedAudioLayer)

    // New selectors
    const selectedZoomRegionId = useEditorStore(state => state.selectedZoomRegionId)
    const setSelectedZoomRegion = useEditorStore(state => state.setSelectedZoomRegion)
    const selectedCameraLayerSelected = useEditorStore(state => state.selectedCameraLayerSelected)
    const setSelectedCameraLayer = useEditorStore(state => state.setSelectedCameraLayer)
    const trackVisibility = useEditorStore(state => state.trackVisibility)
    const toggleTrackVisibility = useEditorStore(state => state.toggleTrackVisibility)

    const duplicateClip = useEditorStore(state => state.duplicateClip)
    const duplicateTextLayer = useEditorStore(state => state.duplicateTextLayer)
    const duplicateAudioLayer = useEditorStore(state => state.duplicateAudioLayer)
    const duplicateZoomRegion = useEditorStore(state => state.duplicateZoomRegion)

    const copySelectedItem = useEditorStore(state => state.copySelectedItem)
    const pasteClipboardItem = useEditorStore(state => state.pasteClipboardItem)

    const projects = useProjectStore(state => state.projects)
    const currentProjectId = useProjectStore(state => state.currentProjectId)
    const currentProject = projects.find(p => p.id === currentProjectId)
    const fileName = currentProject?.name ? `${currentProject.name.replace(/\s+/g, '_')}.mp4` : 'Recording.mp4'

    const [timelineScale, setTimelineScale] = useState(50) // Pixels per second
    const [snapLineTime, setSnapLineTime] = useState<number | null>(null)
    const [hoverTime, setHoverTime] = useState<number | null>(null)
    const [dragTooltip, setDragTooltip] = useState<{ x: number, y: number, label: string, sublabel?: string } | null>(null)
    const [speedPopupClipId, setSpeedPopupClipId] = useState<string | null>(null)

    const timelineRef = useRef<HTMLDivElement>(null)
    const audioInputRef = useRef<HTMLInputElement>(null)
    const sfxInputRef = useRef<HTMLInputElement>(null)

    // Selection Synchronization Helpers
    const selectClip = useCallback((id: string | null) => {
        setSelectedClip(id)
    }, [setSelectedClip])

    const selectTextLayer = useCallback((id: string | null) => {
        setSelectedTextLayer(id)
    }, [setSelectedTextLayer])

    const selectElementLayer = useCallback((id: string | null) => {
        setSelectedElementLayer(id)
    }, [setSelectedElementLayer])

    const selectAudioLayer = useCallback((id: string | null) => {
        setSelectedAudioLayer(id)
    }, [setSelectedAudioLayer])

    const selectZoomRegion = useCallback((id: string | null) => {
        setSelectedZoomRegion(id)
    }, [setSelectedZoomRegion])

    const selectCameraLayer = useCallback((selected: boolean) => {
        setSelectedCameraLayer(selected)
    }, [setSelectedCameraLayer])

    const clearAllSelections = useCallback(() => {
        setSelectedClip(null)
        setSelectedTextLayer(null)
        setSelectedElementLayer(null)
        setSelectedAudioLayer(null)
        setSelectedZoomRegion(null)
        setSelectedCameraLayer(false)
    }, [setSelectedClip, setSelectedTextLayer, setSelectedElementLayer, setSelectedAudioLayer, setSelectedZoomRegion, setSelectedCameraLayer])

    const handleImportAudio = () => audioInputRef.current?.click()
    const handleImportSFX = () => sfxInputRef.current?.click()

    const handleAudioFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const url = URL.createObjectURL(file)

        // Decode audio for waveform + duration
        const arrayBuffer = await file.arrayBuffer()
        const audioCtx = new AudioContext()
        const decoded = await audioCtx.decodeAudioData(arrayBuffer)
        await audioCtx.close()

        // Downsample to ~120 normalized bars
        const channelData = decoded.getChannelData(0)
        const bars = 120
        const blockSize = Math.floor(channelData.length / bars)
        const waveform = Array.from({ length: bars }, (_, i) => {
            let sum = 0
            for (let j = 0; j < blockSize; j++) sum += Math.abs(channelData[i * blockSize + j] || 0)
            return sum / blockSize
        })
        const maxAmp = Math.max(...waveform, 0.001)
        const normalizedWaveform = waveform.map(v => v / maxAmp)

        // Place new clip right after the last existing audio clip
        const nextOffset = audioLayers.reduce((max, l) => Math.max(max, l.offset + l.duration), 0)

        addAudioLayer({
            name: file.name.replace(/\.[^/.]+$/, ''),
            type: 'music',
            offset: nextOffset,
            duration: decoded.duration,
            trimStart: 0,
            volume: 1,
            muted: false,
            url,
            waveform: normalizedWaveform,
            originalDuration: decoded.duration,
        })
        e.target.value = ''
    }

    const handleSFXFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const url = URL.createObjectURL(file)

        // Decode audio for waveform + duration
        const arrayBuffer = await file.arrayBuffer()
        const audioCtx = new AudioContext()
        const decoded = await audioCtx.decodeAudioData(arrayBuffer)
        await audioCtx.close()

        // Downsample to ~120 normalized bars
        const channelData = decoded.getChannelData(0)
        const bars = 120
        const blockSize = Math.floor(channelData.length / bars)
        const waveform = Array.from({ length: bars }, (_, i) => {
            let sum = 0
            for (let j = 0; j < blockSize; j++) sum += Math.abs(channelData[i * blockSize + j] || 0)
            return sum / blockSize
        })
        const maxAmp = Math.max(...waveform, 0.001)
        const normalizedWaveform = waveform.map(v => v / maxAmp)

        // Place sound effects at current playhead currentTime
        const currentTime = useEditorStore.getState().currentTime

        addAudioLayer({
            name: file.name.replace(/\.[^/.]+$/, ''),
            type: 'sfx',
            offset: currentTime,
            duration: decoded.duration,
            trimStart: 0,
            volume: 1,
            muted: false,
            url,
            waveform: normalizedWaveform,
            originalDuration: decoded.duration,
        })
        e.target.value = ''
    }

    const handleRemoveAudioGap = (afterLayerId: string) => {
        const sorted = [...audioLayers].sort((a, b) => a.offset - b.offset)
        const idx = sorted.findIndex(l => l.id === afterLayerId)
        if (idx < 0 || idx >= sorted.length - 1) return
        const current = sorted[idx]
        const next = sorted[idx + 1]
        const gapSize = next.offset - (current.offset + current.duration)
        if (gapSize <= 0.1) return
        for (let i = idx + 1; i < sorted.length; i++) {
            updateAudioLayer(sorted[i].id, { offset: sorted[i].offset - gapSize })
        }
    }

    // Drag / Trim Interaction State References
    const isDraggingPlayhead = useRef(false)
    const interactingWithClip = useRef<{
        id: string,
        type: 'move' | 'trim-left' | 'trim-right',
        startX: number,
        initialStart: number,
        initialEnd: number,
        initialOffset: number
    } | null>(null)

    const interactingWithZoom = useRef<{
        id: string,
        type: 'resize-left' | 'resize-right' | 'move',
        startX: number,
        initialStart: number,
        initialEnd: number
    } | null>(null)

    const interactingWithText = useRef<{
        id: string,
        type: 'resize-left' | 'resize-right' | 'move',
        startX: number,
        initialStart: number,
        initialEnd: number
    } | null>(null)

    const interactingWithElement = useRef<{
        id: string
        type: 'resize-left' | 'resize-right' | 'move'
        startX: number
        initialStart: number
        initialEnd: number
    } | null>(null)

    const interactingWithCamera = useRef<{
        type: 'resize-left' | 'resize-right' | 'move',
        startX: number,
        initialStart: number,
        initialEnd: number
    } | null>(null)

    const interactingWithAudio = useRef<{
        id: string,
        type: 'resize-left' | 'resize-right' | 'move',
        startX: number,
        initialOffset: number,
        initialDuration: number,
        initialTrimStart: number,
    } | null>(null)

    const togglePlay = () => setIsPlaying(!isPlaying)

    const formatTime = (seconds: number) => {
        if (!Number.isFinite(seconds) || seconds < 0) return "00:00"
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    }

    const formatTimeWithMillis = (seconds: number) => {
        if (!Number.isFinite(seconds) || seconds < 0) return "00:00.0"
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        const millis = Math.floor((seconds % 1) * 10)
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${millis}`
    }

    // --- Time Calculation & Magnetic Edge Snapping ---
    const getTimelineTime = useCallback((clientX: number, rect: DOMRect) => {
        if (!timelineRef.current) return 0
        const scrollLeft = timelineRef.current.scrollLeft
        const x = clientX - rect.left + scrollLeft - TIMELINE_PADDING
        return Math.max(0, Math.min(duration, x / timelineScale))
    }, [duration, timelineScale])

    const getSnapPoints = useCallback((ignoreIds: string[] = []): number[] => {
        const pointsArr: number[] = [0, duration, currentTime];

        // Clips start/end
        clips.forEach((c) => {
            if (c.status === 'deleted' || ignoreIds.includes(c.id)) return
            const cDur = (c.end - c.start) / (c.speed ?? 1)
            pointsArr.push(c.offset)
            pointsArr.push(c.offset + cDur)
        })

        // Zoom regions
        zoomRegions.forEach(z => {
            if (ignoreIds.includes(z.id)) return
            pointsArr.push(z.startTime)
            pointsArr.push(z.endTime)
        })

        // Text layers
        textLayers.forEach(t => {
            if (ignoreIds.includes(t.id)) return
            pointsArr.push(t.startTime)
            pointsArr.push(t.endTime)
        })

        // Camera layer
        if (cameraLayer.visible) {
            pointsArr.push(cameraLayer.timeRange?.start ?? 0)
            pointsArr.push(cameraLayer.timeRange?.end ?? duration)
        }

        // Element layers
        elementLayers.forEach(e => {
            if (ignoreIds.includes(e.id)) return
            pointsArr.push(e.startTime)
            pointsArr.push(e.endTime)
        })

        // Audio layers
        audioLayers.forEach(a => {
            if (ignoreIds.includes(a.id)) return
            pointsArr.push(a.offset)
            pointsArr.push(a.offset + a.duration)
        })

        return pointsArr
    }, [clips, zoomRegions, textLayers, elementLayers, cameraLayer, audioLayers, duration, currentTime])

    const calculateMagneticSnap = useCallback((rawTime: number, ignoreIds: string[] = []): { time: number, snapped: boolean } => {
        const thresholdSec = SNAP_THRESHOLD_PX / timelineScale
        const points = getSnapPoints(ignoreIds)
        let closestPoint = rawTime
        let minDiff = Infinity

        for (const pt of points) {
            const diff = Math.abs(pt - rawTime)
            if (diff < minDiff) {
                minDiff = diff
                closestPoint = pt
            }
        }

        if (minDiff <= thresholdSec) {
            return { time: closestPoint, snapped: true }
        }
        return { time: rawTime, snapped: false }
    }, [timelineScale, getSnapPoints])

    // Timeline Click Handler (Playhead positioning / deselecting)
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!timelineRef.current) return
        const target = e.target as HTMLElement

        // Deselect if clicking background
        if (!target.closest('.clip-item') && !target.closest('.zoom-item') && !target.closest('.text-item') && !target.closest('.element-item') && !target.closest('.camera-item') && !target.closest('.audio-clip-item')) {
            clearAllSelections()
        }

        isDraggingPlayhead.current = true
        setIsPlaying(false)

        const rect = timelineRef.current.getBoundingClientRect()
        const rawTime = getTimelineTime(e.clientX, rect)
        const snap = calculateMagneticSnap(rawTime)
        setCurrentTime(snap.time)
        setSnapLineTime(snap.snapped ? snap.time : null)

        const vid = document.getElementById('main-video-player') as HTMLVideoElement
        if (vid) vid.currentTime = snap.time
    }

    // Clip Mouse Down
    const handleClipMouseDown = (e: React.MouseEvent, id: string, type: 'move' | 'trim-left' | 'trim-right') => {
        e.stopPropagation()
        setIsPlaying(false)
        selectClip(id)

        // Pause Zundo history tracking during drag
        useEditorStore.temporal.getState().pause()

        const clip = clips.find(c => c.id === id)
        if (!clip) return

        interactingWithClip.current = {
            id,
            type,
            startX: e.clientX,
            initialStart: clip.start,
            initialEnd: clip.end,
            initialOffset: clip.offset
        }
    }

    // Zoom Mouse Down
    const handleZoomInteractionStart = (e: React.MouseEvent, id: string, type: 'resize-left' | 'resize-right' | 'move', start: number, end: number) => {
        e.stopPropagation()
        setIsPlaying(false)
        selectZoomRegion(id)
        setCurrentTime(start)

        // Pause Zundo history tracking during drag
        useEditorStore.temporal.getState().pause()

        interactingWithZoom.current = {
            id,
            type,
            startX: e.clientX,
            initialStart: start,
            initialEnd: end
        }
    }

    // Camera Mouse Down
    const handleCameraInteractionStart = (e: React.MouseEvent, type: 'resize-left' | 'resize-right' | 'move') => {
        e.stopPropagation()
        setIsPlaying(false)
        selectCameraLayer(true)

        // Pause Zundo history tracking during drag
        useEditorStore.temporal.getState().pause()

        const camStart = cameraLayer.timeRange?.start ?? 0
        const camEnd = cameraLayer.timeRange?.end ?? duration
        interactingWithCamera.current = {
            type,
            startX: e.clientX,
            initialStart: camStart,
            initialEnd: camEnd
        }
    }

    // Text Mouse Down
    const handleTextInteractionStart = (e: React.MouseEvent, id: string, type: 'resize-left' | 'resize-right' | 'move', start: number, end: number) => {
        e.stopPropagation()
        setIsPlaying(false)
        selectTextLayer(id)
        setCurrentTime(start)

        // Pause Zundo history tracking during drag
        useEditorStore.temporal.getState().pause()

        interactingWithText.current = {
            id,
            type,
            startX: e.clientX,
            initialStart: start,
            initialEnd: end
        }
    }

    // Element Mouse Down (temporarily hidden track)
    /*
    const _handleElementInteractionStart = (e: React.MouseEvent, id: string, type: 'resize-left' | 'resize-right' | 'move', start: number, end: number) => {
        e.stopPropagation()
        setIsPlaying(false)
        selectElementLayer(id)
        setCurrentTime(start)

        // Pause Zundo history tracking during drag
        useEditorStore.temporal.getState().pause()

        interactingWithElement.current = {
            id,
            type,
            startX: e.clientX,
            initialStart: start,
            initialEnd: end
        }
    }
    */

    // Audio Mouse Down
    const handleAudioInteractionStart = (e: React.MouseEvent, id: string, type: 'resize-left' | 'resize-right' | 'move') => {
        e.stopPropagation()
        setIsPlaying(false)
        selectAudioLayer(id)

        // Pause Zundo history tracking during drag
        useEditorStore.temporal.getState().pause()

        const layer = audioLayers.find(l => l.id === id)
        if (!layer) return
        interactingWithAudio.current = {
            id,
            type,
            startX: e.clientX,
            initialOffset: layer.offset,
            initialDuration: layer.duration,
            initialTrimStart: layer.trimStart ?? 0,
        }
    }

    // --- Mouse Move RAF Loop & Drag Logic ---
    useEffect(() => {
        let animationFrameId: number | null = null

        const handleMouseMove = (e: MouseEvent) => {
            if (!timelineRef.current) return
            const rect = timelineRef.current.getBoundingClientRect()

            // Update Hover Timestamp Guide
            const hoverRaw = getTimelineTime(e.clientX, rect)
            setHoverTime(hoverRaw)

            // Cancel any pending RAF frame to batch updates smoothly
            if (animationFrameId) cancelAnimationFrame(animationFrameId)

            animationFrameId = requestAnimationFrame(() => {
                // Playhead Scrubbing
                if (isDraggingPlayhead.current) {
                    document.body.classList.add('is-dragging-timeline')
                    const snap = calculateMagneticSnap(hoverRaw)
                    setCurrentTime(snap.time)
                    setSnapLineTime(snap.snapped ? snap.time : null)

                    const vid = document.getElementById('main-video-player') as HTMLVideoElement
                    if (vid) vid.currentTime = snap.time
                    return
                }

                // Video Clip Interaction
                if (interactingWithClip.current) {
                    document.body.classList.add('is-dragging-timeline')
                    const { id, type, startX, initialStart, initialEnd, initialOffset } = interactingWithClip.current
                    const deltaPixels = e.clientX - startX
                    const deltaTime = deltaPixels / timelineScale

                    const clip = clips.find(c => c.id === id)
                    if (!clip) return
                    const speed = clip.speed ?? 1
                    const clipDuration = (initialEnd - initialStart) / speed

                    if (type === 'move') {
                        const targetOffset = Math.max(0, initialOffset + deltaTime)
                        const snap = calculateMagneticSnap(targetOffset, [id])
                        updateClip(id, { offset: snap.time })
                        setSnapLineTime(snap.snapped ? snap.time : null)

                        setDragTooltip({
                            x: e.clientX,
                            y: rect.top - 36,
                            label: `Start: ${formatTimeWithMillis(snap.time)}`,
                            sublabel: `Duration: ${clipDuration.toFixed(1)}s`
                        })
                    } else if (type === 'trim-left') {
                        const maxChange = initialEnd - initialStart - 0.1
                        const cleanDelta = Math.min(deltaTime, maxChange)
                        const finalDelta = Math.max(-initialStart, cleanDelta)

                        const targetOffset = Math.max(0, initialOffset + finalDelta)
                        const snap = calculateMagneticSnap(targetOffset, [id])
                        const snapDelta = snap.time - initialOffset

                        const newStart = initialStart + snapDelta
                        updateClip(id, { start: newStart, offset: snap.time })
                        setSnapLineTime(snap.snapped ? snap.time : null)

                        setDragTooltip({
                            x: e.clientX,
                            y: rect.top - 36,
                            label: `Trim Start: ${formatTimeWithMillis(snap.time)}`,
                            sublabel: `Length: ${((initialEnd - newStart) / speed).toFixed(1)}s`
                        })
                    } else if (type === 'trim-right') {
                        const minChange = -(initialEnd - initialStart) + 0.1
                        const cleanDelta = Math.max(minChange, deltaTime)
                        const maxEnd = sourceDuration || duration
                        const rawEnd = Math.min(maxEnd, initialEnd + (cleanDelta * speed))

                        const clipEndOffset = initialOffset + (rawEnd - initialStart) / speed
                        const snap = calculateMagneticSnap(clipEndOffset, [id])
                        const finalEnd = snap.snapped ? initialStart + (snap.time - initialOffset) * speed : rawEnd

                        updateClip(id, { end: finalEnd })
                        setSnapLineTime(snap.snapped ? snap.time : null)

                        setDragTooltip({
                            x: e.clientX,
                            y: rect.top - 36,
                            label: `End: ${formatTimeWithMillis(initialOffset + (finalEnd - initialStart) / speed)}`,
                            sublabel: `Length: ${((finalEnd - initialStart) / speed).toFixed(1)}s`
                        })
                    }
                    return
                }

                // Zoom Interaction
                if (interactingWithZoom.current) {
                    document.body.classList.add('is-dragging-timeline')
                    const { id, type, startX, initialStart, initialEnd } = interactingWithZoom.current
                    const deltaPixels = e.clientX - startX
                    const deltaTime = deltaPixels / timelineScale

                    const region = zoomRegions.find(r => r.id === id)
                    if (region) {
                        const SAFE_GAP = 0.05
                        const MIN_ZOOM_DUR = 0.2
                        const otherZooms = zoomRegions.filter(r => r.id !== id).sort((a, b) => a.startTime - b.startTime)
                        const prevZoom = [...otherZooms].reverse().find(r => r.endTime <= initialStart + 0.01)
                        const nextZoom = otherZooms.find(r => r.startTime >= initialEnd - 0.01)

                        const minAllowedStart = prevZoom ? prevZoom.endTime + SAFE_GAP : 0
                        const maxAllowedEnd = nextZoom ? nextZoom.startTime - SAFE_GAP : duration

                        if (type === 'move') {
                            const durationLen = initialEnd - initialStart
                            const rawTargetStart = initialStart + deltaTime
                            const snap = calculateMagneticSnap(rawTargetStart, [id])
                            const clampedStart = Math.max(minAllowedStart, Math.min(maxAllowedEnd - durationLen, snap.time))
                            const clampedEnd = clampedStart + durationLen

                            updateZoomRegion(id, { startTime: clampedStart, endTime: clampedEnd })
                            setSnapLineTime(snap.snapped ? clampedStart : null)

                            setDragTooltip({
                                x: e.clientX,
                                y: rect.top - 36,
                                label: `Zoom Start: ${formatTimeWithMillis(clampedStart)}`,
                                sublabel: `Duration: ${durationLen.toFixed(1)}s`
                            })
                        } else if (type === 'resize-left') {
                            const rawTargetStart = initialStart + deltaTime
                            const snap = calculateMagneticSnap(rawTargetStart, [id])
                            const clampedStart = Math.max(minAllowedStart, Math.min(initialEnd - MIN_ZOOM_DUR, snap.time))

                            updateZoomRegion(id, { startTime: clampedStart })
                            setSnapLineTime(snap.snapped ? clampedStart : null)

                            setDragTooltip({
                                x: e.clientX,
                                y: rect.top - 36,
                                label: `Zoom Start: ${formatTimeWithMillis(clampedStart)}`
                            })
                        } else if (type === 'resize-right') {
                            const rawTargetEnd = initialEnd + deltaTime
                            const snap = calculateMagneticSnap(rawTargetEnd, [id])
                            const clampedEnd = Math.min(maxAllowedEnd, Math.max(initialStart + MIN_ZOOM_DUR, snap.time))

                            updateZoomRegion(id, { endTime: clampedEnd })
                            setSnapLineTime(snap.snapped ? clampedEnd : null)

                            setDragTooltip({
                                x: e.clientX,
                                y: rect.top - 36,
                                label: `Zoom End: ${formatTimeWithMillis(clampedEnd)}`
                            })
                        }
                    }
                    return
                }

                // Camera Interaction
                if (interactingWithCamera.current) {
                    document.body.classList.add('is-dragging-timeline')
                    const { type, startX, initialStart, initialEnd } = interactingWithCamera.current
                    const deltaPixels = e.clientX - startX
                    const deltaTime = deltaPixels / timelineScale

                    if (type === 'move') {
                        const len = initialEnd - initialStart
                        const targetStart = Math.max(0, initialStart + deltaTime)
                        const snap = calculateMagneticSnap(targetStart)
                        const newEnd = Math.min(duration, snap.time + len)
                        updateCameraLayer({ timeRange: { start: snap.time, end: newEnd } })
                        setSnapLineTime(snap.snapped ? snap.time : null)

                        setDragTooltip({
                            x: e.clientX,
                            y: rect.top - 36,
                            label: `Camera Start: ${formatTimeWithMillis(snap.time)}`
                        })
                    } else if (type === 'resize-left') {
                        const targetStart = Math.min(Math.max(0, initialStart + deltaTime), initialEnd - 0.5)
                        const snap = calculateMagneticSnap(targetStart)
                        updateCameraLayer({ timeRange: { start: snap.time, end: initialEnd } })
                        setSnapLineTime(snap.snapped ? snap.time : null)
                    } else if (type === 'resize-right') {
                        const targetEnd = Math.max(initialStart + 0.5, Math.min(duration, initialEnd + deltaTime))
                        const snap = calculateMagneticSnap(targetEnd)
                        updateCameraLayer({ timeRange: { start: initialStart, end: snap.time } })
                        setSnapLineTime(snap.snapped ? snap.time : null)
                    }
                    return
                }

                // Text Layer Interaction
                if (interactingWithText.current) {
                    document.body.classList.add('is-dragging-timeline')
                    const { id, type, startX, initialStart, initialEnd } = interactingWithText.current
                    const deltaPixels = e.clientX - startX
                    const deltaTime = deltaPixels / timelineScale

                    const layer = textLayers.find(l => l.id === id)
                    if (layer) {
                        const SAFE_GAP = 0.05
                        const MIN_TEXT_DUR = 0.2
                        const otherTexts = textLayers.filter(l => l.id !== id).sort((a, b) => a.startTime - b.startTime)
                        const prevText = [...otherTexts].reverse().find(l => l.endTime <= initialStart + 0.01)
                        const nextText = otherTexts.find(l => l.startTime >= initialEnd - 0.01)

                        const minAllowedStart = prevText ? prevText.endTime + SAFE_GAP : 0
                        const maxAllowedEnd = nextText ? nextText.startTime - SAFE_GAP : duration

                        if (type === 'move') {
                            const durationLen = initialEnd - initialStart
                            const rawTargetStart = initialStart + deltaTime
                            const snap = calculateMagneticSnap(rawTargetStart, [id])
                            const clampedStart = Math.max(minAllowedStart, Math.min(maxAllowedEnd - durationLen, snap.time))
                            const clampedEnd = clampedStart + durationLen

                            updateTextLayer(id, { startTime: clampedStart, endTime: clampedEnd })
                            setSnapLineTime(snap.snapped ? clampedStart : null)

                            setDragTooltip({
                                x: e.clientX,
                                y: rect.top - 36,
                                label: `Text Start: ${formatTimeWithMillis(clampedStart)}`,
                                sublabel: `Content: "${layer.content}"`
                            })
                        } else if (type === 'resize-left') {
                            const rawTargetStart = initialStart + deltaTime
                            const snap = calculateMagneticSnap(rawTargetStart, [id])
                            const clampedStart = Math.max(minAllowedStart, Math.min(initialEnd - MIN_TEXT_DUR, snap.time))

                            updateTextLayer(id, { startTime: clampedStart })
                            setSnapLineTime(snap.snapped ? clampedStart : null)
                        } else if (type === 'resize-right') {
                            const rawTargetEnd = initialEnd + deltaTime
                            const snap = calculateMagneticSnap(rawTargetEnd, [id])
                            const clampedEnd = Math.min(maxAllowedEnd, Math.max(initialStart + MIN_TEXT_DUR, snap.time))

                            updateTextLayer(id, { endTime: clampedEnd })
                            setSnapLineTime(snap.snapped ? clampedEnd : null)
                        }
                    }
                    return
                }

                // Element Layer Interaction
                if (interactingWithElement.current) {
                    document.body.classList.add('is-dragging-timeline')
                    const { id, type, startX, initialStart, initialEnd } = interactingWithElement.current
                    const deltaPixels = e.clientX - startX
                    const deltaTime = deltaPixels / timelineScale

                    const layer = elementLayers.find(l => l.id === id)
                    if (layer) {
                        const SAFE_GAP = 0.05
                        const MIN_ELEMENT_DUR = 0.2
                        const otherElements = elementLayers.filter(l => l.id !== id).sort((a, b) => a.startTime - b.startTime)
                        const prevElement = [...otherElements].reverse().find(l => l.endTime <= initialStart + 0.01)
                        const nextElement = otherElements.find(l => l.startTime >= initialEnd - 0.01)

                        const minAllowedStart = prevElement ? prevElement.endTime + SAFE_GAP : 0
                        const maxAllowedEnd = nextElement ? nextElement.startTime - SAFE_GAP : duration

                        if (type === 'move') {
                            const durationLen = initialEnd - initialStart
                            const rawTargetStart = initialStart + deltaTime
                            const snap = calculateMagneticSnap(rawTargetStart, [id])
                            const clampedStart = Math.max(minAllowedStart, Math.min(maxAllowedEnd - durationLen, snap.time))
                            const clampedEnd = clampedStart + durationLen

                            updateElementLayer(id, { startTime: clampedStart, endTime: clampedEnd })
                            setSnapLineTime(snap.snapped ? clampedStart : null)

                            setDragTooltip({
                                x: e.clientX,
                                y: rect.top - 36,
                                label: `Element Start: ${formatTimeWithMillis(clampedStart)}`,
                                sublabel: `Type: ${layer.type}`
                            })
                        } else if (type === 'resize-left') {
                            const rawTargetStart = initialStart + deltaTime
                            const snap = calculateMagneticSnap(rawTargetStart, [id])
                            const clampedStart = Math.max(minAllowedStart, Math.min(initialEnd - MIN_ELEMENT_DUR, snap.time))

                            updateElementLayer(id, { startTime: clampedStart })
                            setSnapLineTime(snap.snapped ? clampedStart : null)
                        } else if (type === 'resize-right') {
                            const rawTargetEnd = initialEnd + deltaTime
                            const snap = calculateMagneticSnap(rawTargetEnd, [id])
                            const clampedEnd = Math.min(maxAllowedEnd, Math.max(initialStart + MIN_ELEMENT_DUR, snap.time))

                            updateElementLayer(id, { endTime: clampedEnd })
                            setSnapLineTime(snap.snapped ? clampedEnd : null)
                        }
                    }
                    return
                }

                // Audio Layer Interaction
                if (interactingWithAudio.current) {
                    document.body.classList.add('is-dragging-timeline')
                    const { id, type, startX, initialOffset, initialDuration, initialTrimStart } = interactingWithAudio.current
                    const deltaPixels = e.clientX - startX
                    const deltaTime = deltaPixels / timelineScale
                    const layer = audioLayers.find(l => l.id === id)
                    if (layer) {
                        const SAFE_GAP = 0.05
                        const MIN_AUDIO_DUR = 0.2
                        const isSfx = layer.type === 'sfx'
                        const sameTrackAudio = audioLayers
                            .filter(l => l.id !== id && (isSfx ? l.type === 'sfx' : l.type !== 'sfx'))
                            .sort((a, b) => a.offset - b.offset)

                        const prevAudio = [...sameTrackAudio].reverse().find(l => l.offset + l.duration <= initialOffset + 0.01)
                        const nextAudio = sameTrackAudio.find(l => l.offset >= initialOffset + initialDuration - 0.01)

                        const minAllowedOffset = prevAudio ? prevAudio.offset + prevAudio.duration + SAFE_GAP : 0
                        const maxAllowedEnd = nextAudio ? nextAudio.offset - SAFE_GAP : Infinity

                        if (type === 'move') {
                            const rawTargetOffset = initialOffset + deltaTime
                            const snap = calculateMagneticSnap(rawTargetOffset, [id])
                            const clampedOffset = Math.max(minAllowedOffset, Math.min(maxAllowedEnd - initialDuration, snap.time))

                            updateAudioLayer(id, { offset: clampedOffset })
                            setSnapLineTime(snap.snapped ? clampedOffset : null)

                            setDragTooltip({
                                x: e.clientX,
                                y: rect.top - 36,
                                label: `Audio Offset: ${formatTimeWithMillis(clampedOffset)}`,
                                sublabel: `${layer.name}`
                            })
                        } else if (type === 'resize-left') {
                            const maxDelta = initialDuration - MIN_AUDIO_DUR
                            const clampedDelta = Math.max(-initialTrimStart, Math.min(maxDelta, deltaTime))
                            const rawTargetOffset = initialOffset + clampedDelta
                            const snap = calculateMagneticSnap(rawTargetOffset, [id])

                            const clampedOffset = Math.max(minAllowedOffset, Math.min(initialOffset + initialDuration - MIN_AUDIO_DUR, snap.time))
                            const snapDelta = clampedOffset - initialOffset

                            updateAudioLayer(id, {
                                trimStart: initialTrimStart + snapDelta,
                                offset: clampedOffset,
                                duration: initialDuration - snapDelta,
                            })
                            setSnapLineTime(snap.snapped ? clampedOffset : null)
                        } else if (type === 'resize-right') {
                            const rawTargetDuration = initialDuration + deltaTime
                            const maxFileDuration = (layer.originalDuration ?? layer.duration) - (layer.trimStart ?? 0)
                            const maxDurationAllowed = Math.min(maxAllowedEnd - initialOffset, maxFileDuration)
                            const clampedDuration = Math.max(MIN_AUDIO_DUR, Math.min(maxDurationAllowed, rawTargetDuration))
                            updateAudioLayer(id, { duration: clampedDuration })
                        }
                    }
                    return
                }
            })
        }

        const handleMouseUp = () => {
            const wasInteracting = !!(
                interactingWithClip.current ||
                interactingWithZoom.current ||
                interactingWithText.current ||
                interactingWithElement.current ||
                interactingWithCamera.current ||
                interactingWithAudio.current
            )

            document.body.classList.remove('is-dragging-timeline')
            isDraggingPlayhead.current = false

            if (wasInteracting) {
                // Resume Zundo history tracking after drag ends
                useEditorStore.temporal.getState().resume()
            }

            interactingWithClip.current = null
            interactingWithZoom.current = null
            interactingWithText.current = null
            interactingWithElement.current = null
            interactingWithCamera.current = null
            interactingWithAudio.current = null
            setSnapLineTime(null)
            setDragTooltip(null)
            if (wasInteracting) {
                const temporal = useEditorStore.temporal.getState()
                temporal.resume()
                // Force temporal store to record the final accumulated changes as a single transaction
                const state = useEditorStore.getState()
                state.updateCameraLayer({})
            }
        }

        const handleMouseLeave = () => {
            setHoverTime(null)
        }

        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)
        window.addEventListener('mouseleave', handleMouseLeave)

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId)
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
            window.removeEventListener('mouseleave', handleMouseLeave)
        }
    }, [duration, timelineScale, zoomRegions, updateZoomRegion, clips, updateClip, textLayers, updateTextLayer, elementLayers, updateElementLayer, cameraLayer, updateCameraLayer, audioLayers, updateAudioLayer, calculateMagneticSnap, getTimelineTime, selectClip, selectTextLayer, selectElementLayer, selectAudioLayer])

    // --- Cursor-Centered Mouse Wheel Zoom & Trackpad Pan ---
    useEffect(() => {
        const timelineEl = timelineRef.current
        if (!timelineEl) return

        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) {
                // Zoom centered under cursor time
                e.preventDefault()
                const rect = timelineEl.getBoundingClientRect()
                const scrollLeft = timelineEl.scrollLeft
                const cursorX = e.clientX - rect.left + scrollLeft - TIMELINE_PADDING
                const timeAtCursor = cursorX / timelineScale

                const zoomFactor = e.deltaY < 0 ? 1.15 : 0.85
                const newScale = Math.min(300, Math.max(10, timelineScale * zoomFactor))

                setTimelineScale(newScale)

                // Adjust scroll left so timeAtCursor remains centered under cursor
                requestAnimationFrame(() => {
                    const newScrollLeft = (timeAtCursor * newScale) + TIMELINE_PADDING - (e.clientX - rect.left)
                    timelineEl.scrollLeft = Math.max(0, newScrollLeft)
                })
            } else if (e.shiftKey) {
                // Shift + Wheel horizontal scroll
                e.preventDefault()
                timelineEl.scrollLeft += e.deltaY
            }
        }

        timelineEl.addEventListener('wheel', handleWheel, { passive: false })
        return () => timelineEl.removeEventListener('wheel', handleWheel)
    }, [timelineScale])

    const handleDelete = useCallback(() => {
        if (selectedClipId) {
            deleteClip(selectedClipId)
            selectClip(null)
            return
        }
        if (selectedTextLayerId) {
            removeTextLayer(selectedTextLayerId)
            selectTextLayer(null)
            return
        }
        if (selectedElementLayerId) {
            removeElementLayer(selectedElementLayerId)
            selectElementLayer(null)
            return
        }
        if (selectedAudioLayerId) {
            const layer = audioLayers.find(l => l.id === selectedAudioLayerId)
            removeAudioLayer(selectedAudioLayerId)
            if (layer?.url) URL.revokeObjectURL(layer.url)
            selectAudioLayer(null)
            return
        }
        if (selectedZoomRegionId) {
            removeZoomRegion(selectedZoomRegionId)
            selectZoomRegion(null)
            return
        }
        if (selectedCameraLayerSelected) {
            updateCameraLayer({ visible: false })
            selectCameraLayer(false)
            return
        }
        // Fallback: delete clip at current playhead time
        const clip = clips.find(c => currentTime >= c.offset && currentTime < c.offset + ((c.end - c.start) / (c.speed ?? 1)) && c.status === 'visible')
        if (clip) {
            deleteClip(clip.id)
        }
    }, [
        selectedClipId, deleteClip, selectClip,
        selectedTextLayerId, removeTextLayer, selectTextLayer,
        selectedElementLayerId, removeElementLayer, selectElementLayer,
        selectedAudioLayerId, audioLayers, removeAudioLayer, selectAudioLayer,
        selectedZoomRegionId, removeZoomRegion, selectZoomRegion,
        selectedCameraLayerSelected, updateCameraLayer, selectCameraLayer,
        clips, currentTime
    ])
     // Keyboard Shortcuts & Frame Nudging
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const activeEl = document.activeElement
            if (activeEl && (['INPUT', 'TEXTAREA', 'SELECT'].includes(activeEl.tagName) || activeEl.getAttribute('contenteditable') === 'true')) return

            if (e.code === 'Space') {
                e.preventDefault()
                togglePlay()
            } else if (e.code === 'KeyS') {
                e.preventDefault()
                splitClip(currentTime)
            } else if (e.code === 'Escape') {
                e.preventDefault()
                clearAllSelections()
            } else if (e.code === 'ArrowLeft') {
                e.preventDefault()
                const step = e.shiftKey ? 1.0 : 0.1
                setCurrentTime(Math.max(0, currentTime - step))
            } else if (e.code === 'ArrowRight') {
                e.preventDefault()
                const step = e.shiftKey ? 1.0 : 0.1
                setCurrentTime(Math.min(duration, currentTime + step))
            } else if (e.code === 'Equal' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                setTimelineScale(prev => Math.min(300, prev + 15))
            } else if (e.code === 'Minus' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                setTimelineScale(prev => Math.max(10, prev - 15))
            } else if (e.code === 'KeyD' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                if (selectedClipId) duplicateClip(selectedClipId)
                else if (selectedTextLayerId) duplicateTextLayer(selectedTextLayerId)
                else if (selectedElementLayerId) useEditorStore.getState().duplicateElementLayer?.(selectedElementLayerId)
                else if (selectedAudioLayerId) duplicateAudioLayer(selectedAudioLayerId)
                else if (selectedZoomRegionId) duplicateZoomRegion(selectedZoomRegionId)
            } else if (e.code === 'KeyC' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                copySelectedItem()
            } else if (e.code === 'KeyV' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                pasteClipboardItem()
            } else if (e.code === 'Delete' || e.code === 'Backspace') {
                e.preventDefault()
                handleDelete()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [
        currentTime, duration, isPlaying, splitClip, setCurrentTime, togglePlay, clearAllSelections,
        selectedClipId, selectedTextLayerId, selectedAudioLayerId, selectedZoomRegionId, selectedCameraLayerSelected,
        duplicateClip, duplicateTextLayer, duplicateAudioLayer, duplicateZoomRegion,
        copySelectedItem, pasteClipboardItem, handleDelete
    ])

    // Adding track elements
    const handleZoomTrackClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!timelineRef.current) return
        const time = getTimelineTime(e.clientX, timelineRef.current.getBoundingClientRect())
        const zoomDuration = 3
        const start = Math.max(0, time - (zoomDuration / 2))

        addZoomRegion({
            id: crypto.randomUUID(),
            startTime: start,
            endTime: start + zoomDuration,
            scale: 1.5,
            x: 0,
            y: 0,
            ease: 'easeInOut',
            mode: 'focus'
        })
    }

    const handleTextTrackClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!timelineRef.current) return
        const time = getTimelineTime(e.clientX, timelineRef.current.getBoundingClientRect())
        const textDuration = 3
        const start = Math.max(0, time - (textDuration / 2))

        addTextLayer({
            id: crypto.randomUUID(),
            content: 'Subtitle text',
            startTime: start,
            endTime: start + textDuration,
            fontSize: 20,
            color: '#ffffff',
            fontFamily: 'Outfit',
            fontWeight: '600',
            textAlign: 'center',
            x: 50,
            y: 95,
            opacity: 1,
            letterSpacing: 0,
            lineHeight: 1.2,
            textDecoration: 'none',
            textTransform: 'none',
            strokeEnabled: false,
            textGradient: '',
            animationIn: 'none'
        })
    }

    /*
    const _handleElementTrackClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!timelineRef.current) return
        const time = getTimelineTime(e.clientX, timelineRef.current.getBoundingClientRect())
        const elementDuration = 3
        const start = Math.max(0, time - (elementDuration / 2))

        addElementLayer({
            id: crypto.randomUUID(),
            type: 'arrow',
            startTime: start,
            endTime: start + elementDuration,
            color: '#f97316',
            strokeWidth: 4,
            x: 50,
            y: 50,
            width: 100,
            height: 100,
            rotation: 0,
            opacity: 100,
            animationIn: 'none'
        })
    }
    */

    const handleSplit = () => splitClip(currentTime)

    const handleFitToScreen = () => {
        if (!timelineRef.current || duration <= 0) return
        const containerWidth = timelineRef.current.clientWidth - (TIMELINE_PADDING * 2)
        const fitScale = Math.max(10, Math.min(200, containerWidth / duration))
        setTimelineScale(fitScale)
    }

    const totalWidth = duration * timelineScale + (TIMELINE_PADDING * 2)
    const rulerTicks = Number.isFinite(duration) && duration > 0 ? Math.ceil(duration) + 1 : 0
    const safeRulerTicks = Math.min(rulerTicks, 10000)

    return (
        <div className="h-full bg-surface-950 border-t border-white/5 flex flex-col select-none shadow-[0_-5px_20px_rgba(0,0,0,0.4)] relative">
            {/* Live Floating Drag Tooltip */}
            {dragTooltip && (
                <div
                    className="fixed z-[120] pointer-events-none transform -translate-x-1/2 bg-surface-900/95 backdrop-blur-md border border-brand-500/40 text-white px-3 py-1.5 rounded-lg shadow-2xl flex flex-col items-center gap-0.5"
                    style={{ left: `${dragTooltip.x}px`, top: `${Math.max(10, dragTooltip.y)}px` }}
                >
                    <span className="text-[11px] font-bold font-mono text-brand-300">{dragTooltip.label}</span>
                    {dragTooltip.sublabel && (
                        <span className="text-[9px] text-white/60 font-medium">{dragTooltip.sublabel}</span>
                    )}
                </div>
            )}

            {/* Timeline Toolbar Header */}
            <div className="h-10 border-b border-white/5 flex items-center justify-between px-4 bg-surface-900/80 backdrop-blur-md relative shrink-0 z-20">
                {/* Left: Editing Tools */}
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => useEditorStore.temporal.getState().undo()}
                        className="p-1.5 hover:bg-white/5 rounded-md text-white/40 hover:text-white transition-all active:scale-95"
                        title="Undo (Ctrl+Z)"
                    >
                        <Undo2 size={15} />
                    </button>
                    <button
                        onClick={() => useEditorStore.temporal.getState().redo()}
                        className="p-1.5 hover:bg-white/5 rounded-md text-white/40 hover:text-white transition-all active:scale-95"
                        title="Redo (Ctrl+Y)"
                    >
                        <Redo2 size={15} />
                    </button>

                    <div className="w-px h-4 bg-white/10 mx-2" />

                    <button
                        onClick={handleSplit}
                        className="px-2.5 py-1 hover:bg-brand-500/10 hover:border-brand-500/30 border border-transparent rounded-md flex items-center gap-2 text-white/50 hover:text-brand-300 transition-all group active:scale-95"
                        title="Split Clip at Playhead (S)"
                    >
                        <Scissors size={14} className="group-hover:rotate-12 transition-transform text-brand-400" />
                        <span className="text-[10px] font-bold tracking-widest uppercase">Split</span>
                        <kbd className="flex items-center justify-center px-1 h-4 bg-black/60 border border-white/10 rounded text-[9px] font-bold text-white/40 group-hover:bg-black group-hover:border-white/20 transition-colors">S</kbd>
                    </button>

                    <button
                        onClick={handleDelete}
                        className="px-2 py-1 hover:bg-danger-500/15 hover:border-danger-500/30 border border-transparent rounded-md flex items-center gap-1.5 text-white/40 hover:text-danger-400 transition-all group active:scale-95"
                        title="Delete Selected Item (Delete)"
                    >
                        <Trash2 size={14} className="group-hover:scale-110 transition-transform" />
                        <span className="text-[10px] font-bold tracking-widest uppercase">Delete</span>
                    </button>
                </div>

                {/* Center: Play / Pause Control */}
                <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
                    <button
                        onClick={togglePlay}
                        className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 hover:scale-105 active:scale-95 ${isPlaying ? 'bg-white/10 text-white shadow-inner' : 'bg-brand-500 text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]'}`}
                        title="Play / Pause (Space)"
                    >
                        {isPlaying ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" className="ml-0.5" />}
                    </button>
                </div>

                {/* Right: Scale Controls & Time Readout */}
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5 bg-black/40 px-2 py-1 rounded-lg border border-white/5">
                        <button
                            onClick={() => setTimelineScale(prev => Math.max(10, prev - 10))}
                            className="text-white/40 hover:text-white text-xs px-1 hover:bg-white/10 rounded transition-colors"
                            title="Zoom Out (Ctrl+-)"
                        >-</button>
                        <input
                            type="range"
                            min={10}
                            max={200}
                            value={timelineScale}
                            onChange={(e) => setTimelineScale(Number(e.target.value))}
                            className="w-16 h-1 accent-brand-500 bg-white/10 rounded-full cursor-pointer"
                        />
                        <button
                            onClick={() => setTimelineScale(prev => Math.min(300, prev + 10))}
                            className="text-white/40 hover:text-white text-xs px-1 hover:bg-white/10 rounded transition-colors"
                            title="Zoom In (Ctrl+=)"
                        >+</button>
                        <button
                            onClick={handleFitToScreen}
                            className="p-1 text-white/30 hover:text-brand-300 rounded hover:bg-white/5 transition-colors ml-1"
                            title="Fit Timeline to Screen"
                        >
                            <Maximize2 size={12} />
                        </button>
                    </div>

                    <div className="flex items-center gap-1 bg-black/40 px-3 py-1 rounded-lg border border-white/5 font-mono shadow-inner">
                        <span className="text-xs font-bold text-brand-400 tabular-nums">
                            {formatTime(currentTime)}
                            <span className="text-white/30 text-[10px] ml-0.5">.{String(Math.floor((currentTime % 1) * 10))}</span>
                        </span>
                        <span className="text-xs text-white/20 mx-1">/</span>
                        <span className="text-xs text-white/40 tabular-nums">
                            {formatTime(duration)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Main Timeline Scrollable Area */}
            <div className="flex-1 flex overflow-hidden">
                {/* Fixed Track Headers Sidebar */}
                <div className="w-36 bg-surface-900 border-r border-white/5 flex flex-col select-none z-20 shrink-0 overflow-hidden shadow-lg">
                    {/* Top corner cell (LAYERS removed for ultra-clean look) */}
                    <div className="h-7 border-b border-white/[0.04] bg-surface-900/60 shrink-0" />
                    
                    <div className="flex flex-col overflow-hidden">
                        {/* Video Track Header */}
                        <div className="h-14 flex items-center justify-between px-3 border-b border-white/[0.04] shrink-0" style={{ backgroundColor: 'rgba(69,26,3,0.15)' }}>
                            <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: '#fbbf24' }}>
                                <Film size={14} className="shrink-0" style={{ color: '#fbbf24' }} />
                                Video
                            </span>
                            <button
                                onClick={() => toggleTrackVisibility('video')}
                                className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                style={{ color: trackVisibility.video ? '#fbbf24' : 'rgba(255,255,255,0.2)' }}
                                title={trackVisibility.video ? 'Hide Video' : 'Show Video'}
                            >
                                {trackVisibility.video ? <EyeIcon /> : <EyeOffIcon />}
                            </button>
                        </div>
                        {/* Element Track Header */}
                        {/* <div className="h-11 flex items-center justify-between px-3 border-b border-white/[0.04] shrink-0" style={{ backgroundColor: 'rgba(6,78,59,0.15)' }}>
                            <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: '#10b981' }}>
                                <ShapesIcon size={13} className="shrink-0" style={{ color: '#10b981' }} />
                                Elements
                            </span>
                            <button
                                onClick={() => toggleTrackVisibility('elements')}
                                className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                style={{ color: trackVisibility.elements ? '#10b981' : 'rgba(255,255,255,0.2)' }}
                                title={trackVisibility.elements ? 'Hide Elements' : 'Show Elements'}
                            >
                                {trackVisibility.elements ? <EyeIcon /> : <EyeOffIcon />}
                            </button>
                        </div> */}
                        {/* Zoom Track Header */}
                        <div className="h-11 flex items-center justify-between px-3 border-b border-white/[0.04] shrink-0" style={{ backgroundColor: 'rgba(46,16,101,0.15)' }}>
                            <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: '#a78bfa' }}>
                                <ZoomIn size={13} className="shrink-0" style={{ color: '#a78bfa' }} />
                                Zoom
                            </span>
                            <button
                                onClick={() => toggleTrackVisibility('zoom')}
                                className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                style={{ color: trackVisibility.zoom ? '#a78bfa' : 'rgba(255,255,255,0.2)' }}
                                title={trackVisibility.zoom ? 'Hide Zoom FX' : 'Show Zoom FX'}
                            >
                                {trackVisibility.zoom ? <EyeIcon /> : <EyeOffIcon />}
                            </button>
                        </div>
                        {/* Text Track Header */}
                        <div className="h-11 flex items-center justify-between px-3 border-b border-white/[0.04] shrink-0" style={{ backgroundColor: 'rgba(67,20,7,0.15)' }}>
                            <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: '#fb923c' }}>
                                <Type size={13} className="shrink-0" style={{ color: '#fb923c' }} />
                                Subtitles
                            </span>
                            <button
                                onClick={() => toggleTrackVisibility('text')}
                                className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                style={{ color: trackVisibility.text ? '#fb923c' : 'rgba(255,255,255,0.2)' }}
                                title={trackVisibility.text ? 'Hide Subtitles' : 'Show Subtitles'}
                            >
                                {trackVisibility.text ? <EyeIcon /> : <EyeOffIcon />}
                            </button>
                        </div>

                        {/* Camera Track Header */}
                        {cameraLayer.visible && (
                            <div className="h-11 flex items-center justify-between px-3 border-b border-white/[0.04] shrink-0" style={{ backgroundColor: 'rgba(30,27,75,0.15)' }}>
                                <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: '#818cf8' }}>
                                    <Camera size={13} className="shrink-0" style={{ color: '#818cf8' }} />
                                    Webcam
                                </span>
                                <button
                                    onClick={() => toggleTrackVisibility('camera')}
                                    className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                    style={{ color: trackVisibility.camera ? '#818cf8' : 'rgba(255,255,255,0.2)' }}
                                    title={trackVisibility.camera ? 'Hide Webcam' : 'Show Webcam'}
                                >
                                    {trackVisibility.camera ? <EyeIcon /> : <EyeOffIcon />}
                                </button>
                            </div>
                        )}
                        {/* Audio Track Headers: Music & Sound Effects */}
                        <div className="h-11 flex items-center justify-between px-3 border-b border-white/[0.04] shrink-0" style={{ backgroundColor: 'rgba(8,51,68,0.15)' }}>
                            <input ref={audioInputRef} type="file" accept="audio/*" className="hidden" onChange={handleAudioFileChange} />
                            <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: '#06b6d4' }}>
                                <Music size={13} className="shrink-0" style={{ color: '#06b6d4' }} />
                                Music
                            </span>
                            <button
                                onClick={() => toggleTrackVisibility('music')}
                                className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                style={{ color: trackVisibility.music ? '#06b6d4' : 'rgba(255,255,255,0.2)' }}
                                title={trackVisibility.music ? 'Mute Music' : 'Unmute Music'}
                            >
                                {trackVisibility.music ? <SpeakerIcon /> : <SpeakerOffIcon />}
                            </button>
                        </div>
                        <div className="h-11 flex items-center justify-between px-3 border-b border-white/[0.04] shrink-0" style={{ backgroundColor: 'rgba(80,7,43,0.15)' }}>
                            <input ref={sfxInputRef} type="file" accept="audio/*" className="hidden" onChange={handleSFXFileChange} />
                            <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: '#f472b6' }}>
                                <Volume2 size={13} className="shrink-0" style={{ color: '#f472b6' }} />
                                SFX
                            </span>
                            <div className="flex items-center gap-0.5">
                                <button
                                    onClick={handleImportSFX}
                                    className="p-1.5 rounded-lg transition-colors hover:bg-white/10 text-pink-400/80 hover:text-pink-300"
                                    title="Import Custom SFX File"
                                >
                                    <Upload size={13} />
                                </button>
                                <button
                                    onClick={() => toggleTrackVisibility('sfx')}
                                    className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                    style={{ color: trackVisibility.sfx ? '#f472b6' : 'rgba(255,255,255,0.2)' }}
                                    title={trackVisibility.sfx ? 'Mute SFX' : 'Unmute SFX'}
                                >
                                    {trackVisibility.sfx ? <SpeakerIcon /> : <SpeakerOffIcon />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Timeline Canvas & Tracks Scroll Area */}
                <div
                    ref={timelineRef}
                    className="flex-1 overflow-x-auto overflow-y-hidden relative custom-scrollbar bg-surface-950"
                    onMouseDown={handleMouseDown}
                >
                    <div style={{ width: `${Math.max(totalWidth, 100)}px`, minWidth: '100%' }} className="h-full relative">

                        {/* Ruler Header Bar (Minimal, 28px height) */}
                        <div className="h-7 border-b border-white/[0.04] relative bg-surface-900/40 shadow-sm pointer-events-none select-none shrink-0 overflow-hidden">
                            {Array.from({ length: safeRulerTicks }).map((_, i) => {
                                const isMajor = i % 5 === 0
                                return (
                                    <div
                                        key={i}
                                        className={`absolute bottom-0 w-[1px] ${isMajor ? 'bg-white/40 h-2.5' : 'bg-white/10 h-1.5'}`}
                                        style={{ left: `${TIMELINE_PADDING + i * timelineScale}px` }}
                                    >
                                        {isMajor && (
                                            <span className="absolute bottom-2.5 left-1/2 -translate-x-1/2 text-[8.5px] font-mono text-white/40 tabular-nums">
                                                {formatTime(i)}
                                            </span>
                                        )}
                                    </div>
                                )
                            })}
                        </div>

                        {/* Hover Playhead Guide Line */}
                        {hoverTime !== null && (
                            <div
                                className="timeline-hover-line"
                                style={{ left: `${TIMELINE_PADDING + hoverTime * timelineScale}px` }}
                            >
                                <div className="absolute top-1 left-1/2 -translate-x-1/2 px-1 py-0.5 bg-white/20 backdrop-blur-sm rounded text-[8px] font-mono text-white/80 font-bold">
                                    {formatTimeWithMillis(hoverTime)}
                                </div>
                            </div>
                        )}

                        {/* Magnetic Snap Guide Line */}
                        {snapLineTime !== null && (
                            <div
                                className="timeline-snap-line"
                                style={{ left: `${TIMELINE_PADDING + snapLineTime * timelineScale}px` }}
                            />
                        )}

                        {/* Main Playhead Indicator */}
                        <div
                            className="absolute top-0 bottom-0 z-50 pointer-events-none"
                            style={{ left: `${TIMELINE_PADDING + currentTime * timelineScale}px`, transform: 'translateX(-50%)' }}
                        >
                            <div className="w-0.5 h-full bg-brand-500" />
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-brand-500 rotate-45 -mt-1.5 rounded-sm" />
                        </div>


                {/* Tracks Container */}
                        <div className="py-0 relative flex-1">

                            {/* 1. VIDEO TRACK */}
                            <div className="h-14 relative overflow-hidden group border-b border-white/[0.04] shrink-0" style={{ backgroundColor: 'rgba(69,26,3,0.05)' }}>
                                {(clips || []).map((clip) => {
                                    if (clip.status === 'deleted') return null
                                    const speed = clip.speed ?? 1
                                    const clipDuration = (clip.end - clip.start) / speed
                                    const width = clipDuration * timelineScale
                                    const isSelected = clip.id === selectedClipId

                                    return (
                                        <div
                                            key={clip.id}
                                            className={`absolute top-1.5 bottom-1.5 rounded-2xl overflow-visible group clip-item transition-all duration-150 cursor-grab active:cursor-grabbing
                                                ${isSelected
                                                    ? 'z-20 scale-[1.005] shadow-[0_4px_12px_rgba(0,0,0,0.5)]'
                                                    : 'z-10'}
                                                ${!trackVisibility.video ? 'opacity-35 grayscale-[20%]' : ''}
                                            `}
                                            style={{
                                                left: `${TIMELINE_PADDING + clip.offset * timelineScale}px`,
                                                width: `${width}px`,
                                                background: isSelected 
                                                    ? '#d97706' 
                                                    : 'linear-gradient(to right, rgba(217,119,6,0.35), rgba(180,83,9,0.35))',
                                                borderColor: isSelected ? '#ffffff' : 'rgba(245,158,11,0.55)',
                                                borderWidth: isSelected ? '1.5px' : '1px',
                                                borderStyle: 'solid',
                                            }}
                                            onMouseDown={(e) => handleClipMouseDown(e, clip.id, 'move')}
                                        >
                                            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none w-full h-full">
                                                <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

                                                {/* Smart Thumbnail Filmstrip */}
                                                <div className="absolute inset-0 flex pointer-events-none overflow-hidden select-none opacity-80">
                                                    {thumbnails.length > 0 ? (() => {
                                                        const thumbTargetWidth = 100
                                                        const numThumbs = Math.max(1, Math.floor(width / thumbTargetWidth))

                                                        return Array.from({ length: numThumbs }).map((_, i) => {
                                                            const timeInClip = (i / numThumbs) * (clip.end - clip.start)
                                                            const absoluteTime = clip.start + timeInClip
                                                            const thumbIndex = Math.min(
                                                                thumbnails.length - 1,
                                                                Math.floor((absoluteTime / (sourceDuration || duration)) * thumbnails.length)
                                                            )

                                                            return (
                                                                <div
                                                                    key={i}
                                                                    className="h-full flex-1 border-r border-white/5 last:border-r-0 overflow-hidden bg-black/20"
                                                                >
                                                                    <img
                                                                        src={thumbnails[thumbIndex]}
                                                                        alt=""
                                                                        className="h-full w-full object-cover opacity-75"
                                                                        draggable={false}
                                                                    />
                                                                </div>
                                                            )
                                                        })
                                                    })() : (
                                                        <div className="w-full h-full flex opacity-10 bg-black/20">
                                                            {Array.from({ length: Math.min(20, Math.ceil(width / 60)) }).map((_, i) => (
                                                                <div key={i} className="flex-1 border-r border-white/5 flex items-center justify-center">
                                                                    <Film size={12} className="text-white/30" />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Top File Title & Duration Overlay */}
                                                <div className="absolute left-0 right-0 top-0 h-5 px-2 flex items-center bg-black/50 backdrop-blur-md border-b border-white/5 overflow-hidden z-10">
                                                    <Film size={9} className="text-amber-400 mr-1.5 shrink-0" />
                                                    <span className="text-[9px] text-white/90 font-bold truncate tracking-wider flex-1">
                                                        {fileName}
                                                    </span>
                                                    <span className="text-[8px] font-mono text-white/40 mr-1">
                                                        {clipDuration.toFixed(1)}s
                                                    </span>
                                                    <button
                                                        className={`shrink-0 text-[8px] font-bold px-1.5 py-0.5 rounded leading-none transition-all hover:scale-105 active:scale-95 ${speed !== 1 ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
                                                        onClick={(e) => { e.stopPropagation(); setSpeedPopupClipId(speedPopupClipId === clip.id ? null : clip.id) }}
                                                        title="Change Clip Speed"
                                                    >
                                                        {speed}x
                                                    </button>
                                                </div>

                                                {/* Speed Overlay Picker */}
                                                {speedPopupClipId === clip.id && (
                                                    <div
                                                        className="absolute inset-x-0 bottom-0 top-5 z-30 bg-surface-900/95 backdrop-blur-md flex flex-col items-center justify-center gap-1 px-2"
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <span className="text-[8px] text-white/40 uppercase tracking-widest font-bold">Speed Multiplier</span>
                                                        <div className="flex flex-wrap gap-1 justify-center">
                                                            {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3].map(s => (
                                                                <button
                                                                    key={s}
                                                                    className={`px-2 py-0.5 text-[9px] font-bold rounded transition-all active:scale-95 ${speed === s ? 'bg-amber-500 text-white shadow-md' : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'}`}
                                                                    onClick={(e) => { e.stopPropagation(); updateClip(clip.id, { speed: s }); setSpeedPopupClipId(null) }}
                                                                >{s}x</button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Trim Handles */}
                                            {isSelected && (
                                                <>
                                                    <div
                                                        className="absolute inset-y-0 left-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => handleClipMouseDown(e, clip.id, 'trim-left')}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform -translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="absolute inset-y-0 right-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => handleClipMouseDown(e, clip.id, 'trim-right')}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )
                                })}

                                {/* Clip Gaps & Ripple Delete Pills */}
                                {(() => {
                                    const visibleClips = clips.filter(c => c.status === 'visible').sort((a, b) => a.offset - b.offset)
                                    const gaps = []

                                    if (visibleClips.length > 0 && visibleClips[0].offset > 0.1) {
                                        gaps.push({ start: 0, end: visibleClips[0].offset, duration: visibleClips[0].offset })
                                    }

                                    for (let i = 0; i < visibleClips.length - 1; i++) {
                                        const current = visibleClips[i]
                                        const next = visibleClips[i + 1]
                                        const currentEnd = current.offset + (current.end - current.start) / (current.speed ?? 1)

                                        if (next.offset > currentEnd + 0.1) {
                                            gaps.push({
                                                start: currentEnd,
                                                end: next.offset,
                                                duration: next.offset - currentEnd
                                            })
                                        }
                                    }

                                    return gaps.map((gap, i) => (
                                        <div
                                            key={`gap-${i}`}
                                            className="absolute top-1 bottom-1 bg-white/5 border border-dashed border-white/10 hover:border-danger-500/60 hover:bg-danger-500/10 transition-colors z-30 flex items-center justify-center group/gap rounded-xl"
                                            style={{
                                                left: `${TIMELINE_PADDING + gap.start * timelineScale}px`,
                                                width: `${gap.duration * timelineScale}px`
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                    removeGap(gap.start + 0.1)
                                                }}
                                                className="w-6 h-6 rounded-full bg-danger-500 text-white shadow-lg flex items-center justify-center opacity-0 group-hover/gap:opacity-100 transition-all transform hover:scale-110 active:scale-95 cursor-pointer z-50"
                                                title="Close Gap"
                                            >
                                                <Trash2 size={12} />
                                            </button>

                                            {gap.duration * timelineScale > 40 && (
                                                <span className="text-[9px] text-danger-300/80 font-mono opacity-0 group-hover/gap:opacity-100 absolute bottom-1 pointer-events-none font-bold">
                                                    {gap.duration.toFixed(1)}s gap
                                                </span>
                                            )}
                                        </div>
                                    ))
                                })()}
                            </div>

                            {/* 3.5 ELEMENT LAYER TRACK */}
                            {/* <div
                                className="h-11 relative hover:bg-emerald-950/10 transition-colors cursor-crosshair flex items-center border-b border-white/[0.04] shrink-0"
                                onClick={handleElementTrackClick}
                                title="Click to add element layer"
                                style={{ backgroundColor: 'rgba(6,78,59,0.05)' }}
                            >
                                {elementLayers.map((layer) => {
                                    const isSelected = layer.id === selectedElementLayerId
                                    return (
                                        <div
                                            key={layer.id}
                                            className={`element-item absolute top-1.5 bottom-1.5 rounded-2xl cursor-grab active:cursor-grabbing transition-all duration-150 z-20 group flex items-center px-3 shadow-md overflow-visible
                                                ${isSelected
                                                    ? 'z-30 scale-[1.005] shadow-[0_4px_12px_rgba(0,0,0,0.5)]'
                                                    : ''}
                                                ${!trackVisibility.elements ? 'opacity-35 grayscale-[20%]' : ''}
                                            `}
                                            style={{
                                                left: `${TIMELINE_PADDING + layer.startTime * timelineScale}px`,
                                                width: `${Math.max(10, (layer.endTime - layer.startTime) * timelineScale)}px`,
                                                background: isSelected 
                                                    ? '#10b981' 
                                                    : 'linear-gradient(to right, rgba(16,185,129,0.35), rgba(5,150,105,0.35))',
                                                borderColor: isSelected ? '#ffffff' : 'rgba(52,211,153,0.55)',
                                                borderWidth: isSelected ? '1.5px' : '1px',
                                                borderStyle: 'solid',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                selectElementLayer(layer.id)
                                                setCurrentTime(layer.startTime)
                                            }}
                                            onMouseDown={(e) => handleElementInteractionStart(e, layer.id, 'move', layer.startTime, layer.endTime)}
                                        >
                                            <div className="text-[9px] font-semibold text-white truncate pointer-events-none flex items-center gap-1.5 uppercase tracking-wide">
                                                <span className="truncate">{layer.type}</span>
                                            </div>

                                            {/* Trim Handles */}
                                            {/* {isSelected && (
                                                <>
                                                    <div
                                                        className="absolute inset-y-0 left-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation()
                                                            handleElementInteractionStart(e, layer.id, 'resize-left', layer.startTime, layer.endTime)
                                                        }}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform -translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="absolute inset-y-0 right-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation()
                                                            handleElementInteractionStart(e, layer.id, 'resize-right', layer.startTime, layer.endTime)
                                                        }}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                </>
                                            )} */}
                                        {/* </div>
                                    )
                                })}
                            </div> */}

                            {/* 2. ZOOM TRACK */}
                            <div
                                className="h-11 relative hover:bg-violet-950/10 transition-colors cursor-crosshair flex items-center border-b border-white/[0.04] shrink-0"
                                onClick={handleZoomTrackClick}
                                title="Click to add zoom region"
                                style={{ backgroundColor: 'rgba(46,16,101,0.05)' }}
                            >
                                {zoomRegions.map((region) => {
                                    const isSelected = region.id === selectedZoomRegionId
                                    return (
                                        <div
                                            key={region.id}
                                            className={`zoom-item absolute top-1.5 bottom-1.5 rounded-2xl cursor-grab active:cursor-grabbing transition-all duration-150 z-20 group flex items-center px-3 shadow-md overflow-visible
                                                ${isSelected
                                                    ? 'z-30 scale-[1.005] shadow-[0_4px_12px_rgba(0,0,0,0.5)]'
                                                    : ''}
                                                ${!trackVisibility.zoom ? 'opacity-35 grayscale-[20%]' : ''}
                                            `}
                                            style={{
                                                left: `${TIMELINE_PADDING + region.startTime * timelineScale}px`,
                                                width: `${Math.max(4, (region.endTime - region.startTime) * timelineScale)}px`,
                                                background: isSelected 
                                                    ? '#8b5cf6' 
                                                    : 'linear-gradient(to right, rgba(124,58,237,0.35), rgba(109,40,217,0.35))',
                                                borderColor: isSelected ? '#ffffff' : 'rgba(139,92,246,0.55)',
                                                borderWidth: isSelected ? '1.5px' : '1px',
                                                borderStyle: 'solid',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                selectZoomRegion(region.id)
                                            }}
                                            onMouseDown={(e) => handleZoomInteractionStart(e, region.id, 'move', region.startTime, region.endTime)}
                                        >
                                            <div className="text-[9px] font-bold text-white/90 truncate pointer-events-none flex items-center gap-1.5">
                                                <ZoomIn size={10} className={isSelected ? 'text-white' : 'text-violet-300'} />
                                                <span>Zoom {region.scale}x</span>
                                            </div>

                                            {/* Trim Handles */}
                                            {isSelected && (
                                                <>
                                                    <div
                                                        className="absolute inset-y-0 left-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation()
                                                            handleZoomInteractionStart(e, region.id, 'resize-left', region.startTime, region.endTime)
                                                        }}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform -translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="absolute inset-y-0 right-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation()
                                                            handleZoomInteractionStart(e, region.id, 'resize-right', region.startTime, region.endTime)
                                                        }}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>

                            {/* 3. TEXT LAYER TRACK */}
                            <div
                                className="h-11 relative hover:bg-orange-950/10 transition-colors cursor-crosshair flex items-center border-b border-white/[0.04] shrink-0"
                                onClick={handleTextTrackClick}
                                title="Click to add text layer"
                                style={{ backgroundColor: 'rgba(67,20,7,0.05)' }}
                            >
                                {textLayers.map((layer) => {
                                    const isSelected = layer.id === selectedTextLayerId
                                    return (
                                        <div
                                            key={layer.id}
                                            className={`text-item absolute top-1.5 bottom-1.5 rounded-2xl cursor-grab active:cursor-grabbing transition-all duration-150 z-20 group flex items-center px-3 shadow-md overflow-visible
                                                ${isSelected
                                                    ? 'z-30 scale-[1.005] shadow-[0_4px_12px_rgba(0,0,0,0.5)]'
                                                    : ''}
                                                ${!trackVisibility.text ? 'opacity-35 grayscale-[20%]' : ''}
                                            `}
                                            style={{
                                                left: `${TIMELINE_PADDING + layer.startTime * timelineScale}px`,
                                                width: `${Math.max(10, (layer.endTime - layer.startTime) * timelineScale)}px`,
                                                background: isSelected 
                                                    ? '#f97316' 
                                                    : 'linear-gradient(to right, rgba(234,88,12,0.35), rgba(194,65,12,0.35))',
                                                borderColor: isSelected ? '#ffffff' : 'rgba(251,146,60,0.55)',
                                                borderWidth: isSelected ? '1.5px' : '1px',
                                                borderStyle: 'solid',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                selectTextLayer(layer.id)
                                                setCurrentTime(layer.startTime)
                                            }}
                                            onMouseDown={(e) => handleTextInteractionStart(e, layer.id, 'move', layer.startTime, layer.endTime)}
                                        >
                                            <div className="text-[9px] font-semibold text-white truncate pointer-events-none flex items-center gap-1.5">
                                                <Type size={10} className={isSelected ? 'text-white' : 'text-orange-300'} shrink-0 />
                                                <span className="truncate">{layer.content}</span>
                                            </div>

                                            {/* Trim Handles */}
                                            {isSelected && (
                                                <>
                                                    <div
                                                        className="absolute inset-y-0 left-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation()
                                                            handleTextInteractionStart(e, layer.id, 'resize-left', layer.startTime, layer.endTime)
                                                        }}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform -translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="absolute inset-y-0 right-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation()
                                                            handleTextInteractionStart(e, layer.id, 'resize-right', layer.startTime, layer.endTime)
                                                        }}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>


                            {/* 4. CAMERA WEBCAM TRACK */}
                            {cameraLayer.visible && (
                                <div
                                    className="camera-item h-11 relative hover:bg-indigo-950/10 transition-colors flex items-center border-b border-white/[0.04] shrink-0"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        selectCameraLayer(true)
                                    }}
                                    style={{ backgroundColor: 'rgba(30,27,75,0.05)' }}
                                >
                                    {(() => {
                                        const camStart = cameraLayer.timeRange?.start ?? 0
                                        const camEnd = cameraLayer.timeRange?.end ?? duration
                                        const left = TIMELINE_PADDING + camStart * timelineScale
                                        const w = Math.max(10, (camEnd - camStart) * timelineScale)
                                        const isSelected = selectedCameraLayerSelected
                                        return (
                                            <div
                                                className={`absolute top-1.5 bottom-1.5 rounded-2xl cursor-grab active:cursor-grabbing transition-all duration-150 z-20 group flex items-center px-3 shadow-md overflow-visible
                                                    ${isSelected
                                                        ? 'z-30 scale-[1.005] shadow-[0_4px_12px_rgba(0,0,0,0.5)]'
                                                        : ''}
                                                    ${!trackVisibility.camera ? 'opacity-35 grayscale-[20%]' : ''}
                                                `}
                                                style={{
                                                    left: `${left}px`,
                                                    width: `${w}px`,
                                                    background: isSelected 
                                                        ? '#6366f1' 
                                                        : 'linear-gradient(to right, rgba(79,70,229,0.35), rgba(67,56,202,0.35))',
                                                    borderColor: isSelected ? '#ffffff' : 'rgba(99,102,241,0.55)',
                                                    borderWidth: isSelected ? '1.5px' : '1px',
                                                    borderStyle: 'solid',
                                                }}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation()
                                                    handleCameraInteractionStart(e, 'move')
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    selectCameraLayer(true)
                                                }}
                                            >
                                                <div className="text-[9px] font-bold text-white/95 truncate pointer-events-none flex items-center gap-1.5">
                                                    <Camera size={10} className={isSelected ? 'text-white' : 'text-indigo-300'} shrink-0 />
                                                    <span>Webcam</span>
                                                </div>

                                                {/* Trim Handles */}
                                                {isSelected && (
                                                    <>
                                                        <div
                                                            className="absolute inset-y-0 left-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation()
                                                                handleCameraInteractionStart(e, 'resize-left')
                                                            }}
                                                        >
                                                            <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform -translate-x-1/2">
                                                                <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="absolute inset-y-0 right-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation()
                                                                handleCameraInteractionStart(e, 'resize-right')
                                                            }}
                                                        >
                                                            <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform translate-x-1/2">
                                                                <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )
                                    })()}
                                </div>
                            )}

                            {/* 5. MUSIC TRACK ROW */}
                            <div
                                className="h-11 relative border-b border-white/[0.04] shrink-0"
                                onMouseDown={(e) => {
                                    if (!(e.target as HTMLElement).closest('.audio-clip-item')) {
                                        selectAudioLayer(null)
                                    }
                                    e.stopPropagation()
                                }}
                                style={{ backgroundColor: 'rgba(8,51,68,0.05)' }}
                            >
                                {audioLayers.filter(l => l.type !== 'sfx').length === 0 && (
                                    <div className="sticky left-0 w-[calc(100vw-360px)] max-w-full h-11 flex items-center justify-center pointer-events-none z-10">
                                        <button
                                            onClick={handleImportAudio}
                                            className="pointer-events-auto flex items-center gap-1.5 text-[9px] text-white/30 hover:text-white/60 transition-colors select-none font-bold uppercase tracking-wider px-3 py-1 rounded-lg hover:bg-white/5"
                                        >
                                            <Music size={11} />
                                            Click to import background music
                                        </button>
                                    </div>
                                )}
                                {/* Music Track Gaps */}
                                {(() => {
                                    const musicClips = [...audioLayers.filter(l => l.type !== 'sfx')].sort((a, b) => a.offset - b.offset)
                                    return musicClips.slice(0, -1).map((layer, i) => {
                                        const next = musicClips[i + 1]
                                        const gapStart = layer.offset + layer.duration
                                        const gapEnd = next.offset
                                        const gapDuration = gapEnd - gapStart
                                        if (gapDuration < 0.15) return null
                                        const left = TIMELINE_PADDING + gapStart * timelineScale
                                        const w = gapDuration * timelineScale
                                        return (
                                            <div
                                                key={`audio-gap-${layer.id}`}
                                                className="absolute top-1 bottom-1 z-30 flex items-center justify-center group/gap"
                                                style={{ left: `${left}px`, width: `${w}px` }}
                                                onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                <div className="absolute inset-0 rounded border border-dashed border-cyan-500/20 hover:border-cyan-400/40 hover:bg-cyan-500/10 transition-colors" />
                                                {w > 20 && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleRemoveAudioGap(layer.id) }}
                                                        className="opacity-0 group-hover/gap:opacity-100 transition-opacity w-5 h-5 rounded-full flex items-center justify-center text-cyan-300 hover:text-white z-10 bg-cyan-700/80 hover:bg-cyan-600 shadow-md"
                                                        title="Close Audio Gap"
                                                    >
                                                        <Scissors size={9} />
                                                    </button>
                                                )}
                                            </div>
                                        )
                                    })
                                })()}

                                {audioLayers.filter(l => l.type !== 'sfx').map(layer => {
                                    const left = TIMELINE_PADDING + layer.offset * timelineScale
                                    const w = Math.max(10, layer.duration * timelineScale)
                                    const isSelected = layer.id === selectedAudioLayerId
                                    const wf = layer.waveform

                                    return (
                                        <div
                                            key={layer.id}
                                            className={`audio-clip-item absolute top-1.5 bottom-1.5 rounded-2xl z-20 select-none cursor-grab active:cursor-grabbing transition-all duration-150 overflow-visible
                                                ${isSelected
                                                    ? 'z-30 scale-[1.005] shadow-[0_4px_12px_rgba(0,0,0,0.5)]'
                                                    : ''}
                                                ${!trackVisibility.music || layer.muted ? 'opacity-35 grayscale-[20%]' : ''}
                                            `}
                                            style={{
                                                left: `${left}px`,
                                                width: `${w}px`,
                                                background: isSelected 
                                                    ? '#06b6d4' 
                                                    : 'linear-gradient(to right, rgba(8,145,178,0.35), rgba(14,116,144,0.35))',
                                                borderColor: isSelected ? '#ffffff' : 'rgba(6,182,212,0.55)',
                                                borderWidth: isSelected ? '1.5px' : '1px',
                                                borderStyle: 'solid',
                                            }}
                                            onClick={(e) => { e.stopPropagation(); selectAudioLayer(layer.id) }}
                                            onMouseDown={(e) => handleAudioInteractionStart(e, layer.id, 'move')}
                                        >
                                            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none w-full h-full">
                                                <div className="absolute top-0 left-3 right-0 h-[18px] flex items-center px-1.5 gap-1 pointer-events-none">
                                                    <Music size={8} className={isSelected ? 'text-white' : 'text-cyan-300'} shrink-0 />
                                                    <span className={`text-[8px] font-bold truncate uppercase tracking-wider ${isSelected ? 'text-white' : 'text-cyan-100'}`}>{layer.name}</span>
                                                </div>
                                                <div className="absolute left-3 right-3 overflow-hidden" style={{ top: 16, bottom: 2 }}>
                                                    <div className="w-full h-full flex items-center gap-px px-0.5">
                                                        {(() => {
                                                            const targetBars = Math.max(1, Math.floor((w - 24) / 2.5))
                                                            const waveColor = isSelected ? 'rgba(255,255,255,0.85)' : 'rgba(165,243,252,0.7)'
                                                            if (wf && wf.length > 0) {
                                                                const trimStart = layer.trimStart ?? 0
                                                                const sourceDur = trimStart + layer.duration
                                                                const startIdx = Math.floor((trimStart / Math.max(sourceDur, 0.001)) * wf.length)
                                                                const visible = wf.slice(startIdx)
                                                                const step = Math.max(1, visible.length / targetBars)
                                                                return Array.from({ length: targetBars }, (_, i) => {
                                                                    const v = visible[Math.floor(i * step)] ?? 0
                                                                    const pct = Math.max(8, v * 90)
                                                                    return (
                                                                        <div key={i} className="flex-1 flex flex-col items-center justify-center h-full">
                                                                            <div className="w-full rounded-full" style={{ height: `${pct}%`, background: waveColor }} />
                                                                        </div>
                                                                    )
                                                                })
                                                            }
                                                            return Array.from({ length: targetBars }, (_, i) => {
                                                                const pct = 15 + Math.abs(Math.sin(i * 0.35 + 1)) * 70
                                                                return (
                                                                    <div key={i} className="flex-1 flex flex-col items-center justify-center h-full">
                                                                        <div className="w-full rounded-full" style={{ height: `${pct}%`, background: isSelected ? 'rgba(255,255,255,0.6)' : 'rgba(196,181,253,0.5)' }} />
                                                                    </div>
                                                                )
                                                            })
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Trim Handles */}
                                            {isSelected && (
                                                <>
                                                    <div
                                                        className="absolute inset-y-0 left-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation()
                                                            handleAudioInteractionStart(e, layer.id, 'resize-left')
                                                        }}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform -translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="absolute inset-y-0 right-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation()
                                                            handleAudioInteractionStart(e, layer.id, 'resize-right')
                                                        }}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>

                            {/* 6. SOUND EFFECTS TRACK ROW */}
                            <div
                                className="h-11 relative border-b border-white/[0.04] shrink-0"
                                onMouseDown={(e) => {
                                    if (!(e.target as HTMLElement).closest('.audio-clip-item')) {
                                        selectAudioLayer(null)
                                    }
                                    e.stopPropagation()
                                }}
                                style={{ backgroundColor: 'rgba(80,7,43,0.05)' }}
                            >
                                {audioLayers.filter(l => l.type === 'sfx').length === 0 && (
                                    <div className="sticky left-0 w-[calc(100vw-360px)] max-w-full h-11 flex items-center justify-center pointer-events-none z-10">
                                        <button
                                            onClick={handleImportSFX}
                                            className="pointer-events-auto flex items-center gap-1.5 text-[9px] text-white/30 hover:text-white/60 transition-colors select-none font-bold uppercase tracking-wider px-3 py-1 rounded-lg hover:bg-white/5 cursor-pointer"
                                        >
                                            <Volume2 size={11} />
                                            Click to import sound effect (SFX)
                                        </button>
                                    </div>
                                )}

                                {audioLayers.filter(l => l.type === 'sfx').map(layer => {
                                    const left = TIMELINE_PADDING + layer.offset * timelineScale
                                    const w = Math.max(10, layer.duration * timelineScale)
                                    const isSelected = layer.id === selectedAudioLayerId
                                    const wf = layer.waveform

                                    return (
                                        <div
                                            key={layer.id}
                                            className={`audio-clip-item absolute top-1.5 bottom-1.5 rounded-2xl z-20 select-none cursor-grab active:cursor-grabbing transition-all duration-150 overflow-visible
                                                ${isSelected
                                                    ? 'z-30 scale-[1.005] shadow-[0_4px_12px_rgba(0,0,0,0.5)]'
                                                    : ''}
                                                ${!trackVisibility.sfx || layer.muted ? 'opacity-35 grayscale-[20%]' : ''}
                                            `}
                                            style={{
                                                left: `${left}px`,
                                                width: `${w}px`,
                                                background: isSelected 
                                                    ? '#ec4899' 
                                                    : 'linear-gradient(to right, rgba(219,39,119,0.35), rgba(190,24,93,0.35))',
                                                borderColor: isSelected ? '#ffffff' : 'rgba(236,72,153,0.55)',
                                                borderWidth: isSelected ? '1.5px' : '1px',
                                                borderStyle: 'solid',
                                            }}
                                            onClick={(e) => { e.stopPropagation(); selectAudioLayer(layer.id) }}
                                            onMouseDown={(e) => handleAudioInteractionStart(e, layer.id, 'move')}
                                        >
                                            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none w-full h-full">
                                                <div className="absolute top-0 left-3 right-0 h-[18px] flex items-center px-1.5 gap-1 pointer-events-none">
                                                    <Volume2 size={8} className={isSelected ? 'text-white' : 'text-pink-300'} shrink-0 />
                                                    <span className={`text-[8px] font-bold truncate uppercase tracking-wider ${isSelected ? 'text-white' : 'text-pink-100'}`}>{layer.name}</span>
                                                </div>
                                                <div className="absolute left-3 right-3 overflow-hidden" style={{ top: 16, bottom: 2 }}>
                                                    <div className="w-full h-full flex items-center gap-px px-0.5">
                                                        {(() => {
                                                            const targetBars = Math.max(1, Math.floor((w - 24) / 2.5))
                                                            const waveColor = isSelected ? 'rgba(255,255,255,0.85)' : 'rgba(244,114,182,0.7)'
                                                            if (wf && wf.length > 0) {
                                                                const trimStart = layer.trimStart ?? 0
                                                                const sourceDur = trimStart + layer.duration
                                                                const startIdx = Math.floor((trimStart / Math.max(sourceDur, 0.001)) * wf.length)
                                                                const visible = wf.slice(startIdx)
                                                                const step = Math.max(1, visible.length / targetBars)
                                                                return Array.from({ length: targetBars }, (_, i) => {
                                                                    const v = visible[Math.floor(i * step)] ?? 0
                                                                    const pct = Math.max(8, v * 90)
                                                                    return (
                                                                        <div key={i} className="flex-1 flex flex-col items-center justify-center h-full">
                                                                            <div className="w-full rounded-full" style={{ height: `${pct}%`, background: waveColor }} />
                                                                        </div>
                                                                    )
                                                                })
                                                            }
                                                            return Array.from({ length: targetBars }, (_, i) => {
                                                                const pct = 15 + Math.abs(Math.sin(i * 0.35 + 1)) * 70
                                                                return (
                                                                    <div key={i} className="flex-1 flex flex-col items-center justify-center h-full">
                                                                        <div className="w-full rounded-full" style={{ height: `${pct}%`, background: isSelected ? 'rgba(255,255,255,0.6)' : 'rgba(244,114,182,0.5)' }} />
                                                                    </div>
                                                                )
                                                            })
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Trim Handles */}
                                            {isSelected && (
                                                <>
                                                    <div
                                                        className="absolute inset-y-0 left-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation()
                                                            handleAudioInteractionStart(e, layer.id, 'resize-left')
                                                        }}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform -translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="absolute inset-y-0 right-0 w-3 cursor-ew-resize flex items-center justify-center z-30"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation()
                                                            handleAudioInteractionStart(e, layer.id, 'resize-right')
                                                        }}
                                                    >
                                                        <div className="w-2.5 h-6 bg-white border border-neutral-300 rounded shadow-md flex items-center justify-center transform translate-x-1/2">
                                                            <div className="w-0.5 h-3 bg-neutral-400 rounded-full" />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
